<script setup lang="ts">
import type { Ref } from 'vue';
import type { ReadonlyDeep } from 'type-fest';
import type { HeadersData } from '@/composables/useTable';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed, reactive } from 'vue';

import { utcDateStrToUserZonedDateStrFormat } from '@/utils/format';
import KBtn from '@/components/btns/KBtn.vue';
import TableLoader from '@/components/loaders/TableLoader.vue';
import EmDashPlaceholder from '@/components/placeholders/EmDashPlaceholder.vue';
import SortThTableCell from '@/components/tableCells/SortThTableCell.vue';
import TableEmptyText from '@/components/texts/TableEmptyText.vue';
import useTable from '@/composables/useTable';
import { useUserStore } from '@/stores/useUserStore';
import { fetchDeleteDraft } from '@/helpers/fetchers';
import * as routeNames from '@/constants/routeNames';
import { JobDraftsListItem } from '@/models/draft';
import { brandConfig } from '@/brand';

import tableStyles from '@/styles/table.module.scss';

export interface Props {
  loading?: boolean;
  data?: ReadonlyDeep<openapi.JobDraftListContentOpenapi['results']>;
}
const props = withDefaults(defineProps<Props>(), {
  loading: false,
  data: () => [],
});

const formatPattern = 'hh:mm aa, MMM dd, yyyy';

const userStore = useUserStore();

const isDeletingDraft = reactive<Record<number, boolean>>({});

const useTableValues = useTable(props);
const { actives, ascs, handleSort } = useTableValues;
const sortedData = useTableValues.sortedData as Readonly<
  Ref<ReadonlyDeep<JobDraftsListItem[]>>
>;

const headersData = computed<HeadersData>(function () {
  // if (props.isStaff) { // use computed to keep the pattern from other tables
  return [
    {
      value: 'id',
      title: 'Id',
    },
    {
      value: 'name',
      title: 'Title',
    },
    {
      value: 'created_by',
      title: 'Created by',
    },
    {
      value: 'modified',
      title: 'Modified date',
    },
    {
      value: 'created',
      title: 'Created date',
    },
  ];
});

const foldColspan = computed(() => {
  return headersData.value.length + 1;
});

function deleteItem(id: number): void {
  if (isDeletingDraft[id]) return;
  isDeletingDraft[id] = true;
  void fetchDeleteDraft(id)
    .then(() => {
      // TODO
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (sortedData.value == null) return;
      const deletingItemIndex = sortedData.value.findIndex(
        (item) => item.id === id,
      );
      // TODO after sorting the deleted element appears again
      // @ts-expect-error
      sortedData.value.splice(deletingItemIndex, 1);
    })
    .finally(() => {
      /* eslint-disable-next-line @typescript-eslint/no-dynamic-delete */
      delete isDeletingDraft[id];
    });
}

const userTimeZone = computed(() => userStore.user.timezone);

const draftEditRouteName =
  brandConfig.brand === 'pac'
    ? routeNames.DRAFT_PAC_JOB_BOOKING
    : routeNames.DRAFT_ADVANCED_JOB_BOOKING;

function dateStrItemExt(value: unknown): string {
  return utcDateStrToUserZonedDateStrFormat(
    value,
    userTimeZone.value,
    formatPattern,
  );
}
</script>

<template>
  <table
    :class="[tableStyles.table, $style.draftsTable]"
    data-testid="draftsTable"
  >
    <thead>
      <tr>
        <template
          v-for="(headerItem, headerIndex) in headersData"
          :key="headerIndex"
        >
          <SortThTableCell
            :colspan="headerItem.colspan"
            :actives="actives"
            :ascs="ascs"
            :handle-sort="handleSort"
            :header-item-value="headerItem.value"
            :is-not-sortable="headerItem.isNotSortable"
            :class="$style[headerItem.value + 'Th']"
          >
            {{ headerItem.title }}
          </SortThTableCell>
        </template>
        <th :class="$style.actionsTh">Actions</th>
      </tr>
    </thead>
    <tr v-if="props.loading">
      <td :colspan="foldColspan" class="!h-64 p-0">
        <TableLoader />
      </td>
    </tr>
    <tr v-else-if="!sortedData?.length">
      <td :colspan="foldColspan" class="border-b-0 p-0">
        <TableEmptyText />
      </td>
    </tr>
    <tbody v-else>
      <template v-for="dataItem in sortedData" :key="dataItem.unique_key">
        <tr>
          <td class="whitespace-nowrap">
            {{ dataItem.id }}
          </td>
          <td class="w-100">
            <EmDashPlaceholder :item="dataItem.name" />
          </td>
          <td class="whitespace-nowrap">
            <EmDashPlaceholder :item="dataItem.created_by" />
          </td>
          <td class="whitespace-nowrap">
            <EmDashPlaceholder
              :item="dataItem.modified"
              :ext="dateStrItemExt"
            />
          </td>
          <td class="whitespace-nowrap">
            <EmDashPlaceholder :item="dataItem.created" :ext="dateStrItemExt" />
          </td>
          <td :class="$style.actionsTd">
            <menu :class="$style.actionsMenuList">
              <li :class="$style.actionsMenuListItem">
                <KBtn
                  :class="$style.btn"
                  variant="text"
                  size="sm"
                  append-icon="mdi-pencil-outline"
                  :to="{
                    name: draftEditRouteName,
                    params: {
                      id: dataItem.id,
                    },
                  }"
                >
                  Edit
                </KBtn>
              </li>
              <li :class="$style.actionsMenuListItem">
                <KBtn
                  :class="$style.btn"
                  variant="text"
                  size="sm"
                  append-icon="mdi-delete-outline"
                  priority="danger"
                  :loading="isDeletingDraft[dataItem.id]"
                  @click="deleteItem(dataItem.id)"
                >
                  Delete
                </KBtn>
              </li>
            </menu>
          </td>
        </tr>
      </template>
    </tbody>
  </table>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.draftsTable {
  width: 100%;

  & > thead > tr {
    & > th {
      white-space: nowrap;
    }
    & > th {
      position: sticky;
      top: variables.$appBarHeight;
      z-index: 1;
    }
  }
}
  .idTh {
  }
  .nameTh {
  }
  .created_byTh {
  }
  .modifiedTh {
  }
  .createdTh {
  }
  .actionsTh {
  }
  .idTd {
  }
  .nameTd {
  }
  .created_byTd {
  }
  .modifiedTd {
  }
  .createdTd {
  }
  .actionsTd {
    white-space: nowrap;
    text-align: center;
  }
    .actionsMenuList {
      list-style: none;
      padding: 0;
      margin: -12px -10px;
    }
      .actionsMenuListItem {
        display: inline;
      }
        .btn {
          :global {
            .v-btn__content {
              text-transform: none;
            }
          }
        }
</style>
